"use client";

import { useTranslations } from "next-intl";
import type { RefObject } from "react";
import { ButtonLoadingIcon } from "@/components/common/buttons/button-loading-icon";
import { FloatingBottomBar } from "@/components/floating-bottom-bar";
import { getCartTotalPricing } from "@/features/cart/helpers/cart-total-pricing";
import { useGoToCheckout } from "@/features/cart/hooks/use-go-to-checkout";
import { getLocalePrice } from "@/lib/utilities";
import { useCartStore } from "../cart-provider";
import { DeliveryCost } from "../delivery-cost";

export const CheckoutBar = ({
  pricingRef,
}: {
  pricingRef: RefObject<HTMLDivElement | null>;
}) => {
  const t = useTranslations();
  const { activeItems, goToCheckout, isLeaving } = useGoToCheckout();

  const cartStore = useCartStore((state) => state);
  const totalPrice = getCartTotalPricing(cartStore)?.final.gross;
  const cost = cartStore.getDeliveryCost();

  return (
    <FloatingBottomBar
      button={{
        action: goToCheckout,
        label: (
          <span className="flex items-center gap-1.5">
            <span>{t("cart.pricing.cta")}</span>
            <ButtonLoadingIcon className="size-4" loading={isLeaving} />
          </span>
        ),
        disabled: activeItems.length === 0 || isLeaving,
        className: "px-6!",
      }}
      content={
        <div className="flex flex-col justify-center">
          {(!cost || !!cartStore.pricing?.delivery) && (
            <span className="font-medium text-brand-green1-300">
              {t("cart.pricing.totalPrice")}:
            </span>
          )}
          <span className="font-bold text-brand-violet text-lg leading-[26px]">
            {getLocalePrice({
              price: totalPrice ?? 0,
            })}
          </span>

          {cost && (
            <DeliveryCost
              {...cost}
              align="left"
              calculated={!!cartStore.pricing?.delivery}
              hideNotFree
            />
          )}
        </div>
      }
      hidingElementRef={pricingRef}
    />
  );
};
